// Relations for the knowledge base, over the index served by the info
// server's /api/kb (server/src/info-server/KnowledgeBase.ts). Drop matching
// mirrors the world server's LootSystem.matchDropRows.

export interface KbMonster {
    level: number;
    hp: number;
    attack: number;
    defense: number;
    accuracy: number;
    dodge: number;
    exp: number;
    aggressive: number;
    speed: number;
}

export interface KbZone {
    mobs: Record<number, number>;
    npcs: { type: number; x: number; y: number }[];
    warps: { destMap: number; x: number; y: number }[];
}

export interface KbDrop {
    type: number;
    id: number;
    prob: number;
    map: number;
    mob: number;
    minLevel: number;
    maxLevel: number;
}

export interface KbIndex {
    monsters: Record<number, KbMonster>;
    zones: Record<number, KbZone>;
    drops: KbDrop[];
}

// mob: that monster only; zone: anything killed in one zone; level: any
// monster in a level range; global: every kill.
export type DropSource = "mob" | "zone" | "level" | "global";

export function dropSource(d: KbDrop): DropSource {
    if (d.mob) return "mob";
    if (d.map) return "zone";
    if (d.minLevel || d.maxLevel) return "level";
    return "global";
}

function levelMatches(d: KbDrop, level: number): boolean {
    return (d.minLevel === 0 && d.maxLevel === 0) || (level >= d.minLevel && level <= d.maxLevel);
}

// prob is rolled out of 1000 per matching row.
export function chanceLabel(prob: number): string {
    return `${Math.round(prob) / 10}%`;
}

export function monsterZones(index: KbIndex, mob: number): { map: number; groups: number }[] {
    return Object.entries(index.zones)
        .filter(([, zone]) => zone.mobs[mob])
        .map(([map, zone]) => ({ map: Number(map), groups: zone.mobs[mob] }))
        .sort((a, b) => b.groups - a.groups);
}

export function npcZone(index: KbIndex, type: number): { map: number; x: number; y: number } | null {
    for (const [map, zone] of Object.entries(index.zones)) {
        const npc = zone.npcs.find((n) => n.type === type);
        if (npc) return { map: Number(map), x: npc.x, y: npc.y };
    }
    return null;
}

// Every item_drops row a kill of this monster can roll, in any zone it spawns in.
export function dropsForMonster(index: KbIndex, mob: number): KbDrop[] {
    const level = index.monsters[mob]?.level ?? 0;
    const maps = new Set(monsterZones(index, mob).map((z) => z.map));
    return index.drops
        .filter((d) => (d.mob === 0 || d.mob === mob) && (d.map === 0 || maps.has(d.map)) && levelMatches(d, level))
        .sort((a, b) => b.prob - a.prob);
}

export function dropsForItem(index: KbIndex, type: number, id: number): KbDrop[] {
    return index.drops.filter((d) => d.type === type && d.id === id).sort((a, b) => b.prob - a.prob);
}

export function monsterIds(index: KbIndex): number[] {
    const ids = new Set<number>();
    for (const zone of Object.values(index.zones)) {
        for (const mob of Object.keys(zone.mobs)) ids.add(Number(mob));
    }
    return [...ids];
}

// "[Visitor Guide] Arua's Fairy" -> role "Visitor Guide", name "Arua's Fairy".
export function splitNpcName(full: string): { role: string; name: string } {
    const match = full.match(/^\s*\[([^\]]*)\]\s*(.*)$/);
    return match ? { role: match[1], name: match[2] || match[1] } : { role: "", name: full };
}
